// Host Service
// Business logic for host/greeter operations (Phase 2)

import {
    updateQueueDocument,
    updateUserDocument,
    serverTimestamp
} from './firestore-service.js';
import { STATUS, SELLER_STATUS } from '../config/constants.js';

/**
 * Assign a customer to a seller (optional enhancement)
 * Pre-condition: Seller must be available
 * Note: Sellers can still call ANY customer - this is an optional helper
 */
export async function assignCustomerToSeller(customerId, sellerEmail, assignedBy, usePreServing = false) {
    const updateData = {
        assignedTo: sellerEmail,
        assignedBy: assignedBy,
        assignedTimestamp: serverTimestamp()
    };

    // Only use pre-serving status if explicitly requested (when store is packed)
    if (usePreServing) {
        updateData.status = STATUS.PRE_SERVING;
    }

    await updateQueueDocument(customerId, updateData);
}

/**
 * Remove customer assignment (unassign)
 */
export async function unassignCustomer(customerId) {
    await updateQueueDocument(customerId, {
        assignedTo: null,
        assignedBy: null,
        assignedTimestamp: null,
        status: STATUS.WAITING // Return to waiting if was in pre-serving
    });
}

/**
 * Set customer party size
 */
export async function setPartySize(customerId, partySize) {
    if (partySize < 1) {
        throw new Error('Party size must be at least 1');
    }

    await updateQueueDocument(customerId, {
        partySize: parseInt(partySize)
    });
}

/**
 * Toggle customer preferential status
 * (Host can add or remove preferential marking)
 */
export async function togglePreferential(customerId, isPreferential, modifiedBy) {
    await updateQueueDocument(customerId, {
        isPreferential: isPreferential,
        preferentialModifiedBy: modifiedBy,
        preferentialModifiedAt: serverTimestamp()
    });
}

/**
 * Add host note to customer
 */
export async function addHostNote(customerId, note, addedBy) {
    await updateQueueDocument(customerId, {
        hostNote: note,
        hostNoteBy: addedBy,
        hostNoteAt: serverTimestamp()
    });
}

/**
 * Get seller availability status
 * Returns: 'available', 'busy', 'paused', or 'offline'
 */
export function getSellerStatus(seller, allCustomers) {
    if (!seller.isOnline) {
        return SELLER_STATUS.OFFLINE;
    }

    if (seller.isPaused) {
        return SELLER_STATUS.PAUSED;
    }

    // Check if seller is currently serving or calling a customer
    const servingCustomer = allCustomers.find(c =>
        (c.attendedBy === seller.email && c.status === STATUS.SERVING) ||
        (c.calledBy === seller.email && c.status === STATUS.CALLING)
    );

    if (servingCustomer) {
        return SELLER_STATUS.BUSY;
    }

    return SELLER_STATUS.AVAILABLE;
}

/**
 * Get detailed seller information for host view
 * Includes current customer if busy
 */
export function getSellerDetails(seller, allCustomers) {
    const status = getSellerStatus(seller, allCustomers);
    const details = {
        ...seller,
        status: status
    };

    if (status === SELLER_STATUS.BUSY) {
        const currentCustomer = allCustomers.find(c =>
            (c.attendedBy === seller.email && c.status === STATUS.SERVING) ||
            (c.calledBy === seller.email && c.status === STATUS.CALLING)
        );
        details.currentCustomer = currentCustomer;
    }

    return details;
}

/**
 * Calculate time breakdown for a customer
 * Returns: waitingTime, preServingTime, servingTime, totalTime (in seconds)
 */
export function calculateTimeBreakdown(customer) {
    const now = new Date();
    const registeredAt = customer.createdAt?.toDate() || now;
    const calledAt = customer.calledTimestamp?.toDate();
    const attendedAt = customer.attendedAt?.toDate();
    const finishedAt = customer.finishedAt?.toDate() || now;

    const breakdown = {
        waitingTime: 0,      // registered → called
        preServingTime: 0,   // called → serving started
        servingTime: 0,      // serving → finished
        totalTime: 0         // registered → finished
    };

    // Waiting time: registration to when service started (or now if still waiting)
    // This ensures: waitingTime + servingTime = totalTime
    if (attendedAt) {
        // Service started - waiting time is FROZEN at time from registration to service start
        breakdown.waitingTime = Math.round((attendedAt - registeredAt) / 1000);
    } else if (calledAt) {
        // Customer was called but service hasn't started yet - use called time
        breakdown.waitingTime = Math.round((calledAt - registeredAt) / 1000);
    } else if (customer.status === STATUS.WAITING || customer.status === STATUS.PRE_SERVING || customer.status === STATUS.CALLING) {
        // Customer is still waiting - show ongoing time until now
        breakdown.waitingTime = Math.round((now - registeredAt) / 1000);
    }

    // Pre-serving time: called to serving started
    if (calledAt && attendedAt) {
        breakdown.preServingTime = Math.round((attendedAt - calledAt) / 1000);
    } else if (calledAt && customer.status === STATUS.CALLING) {
        breakdown.preServingTime = Math.round((now - calledAt) / 1000);
    }

    // Serving time: serving to finished
    if (attendedAt) {
        breakdown.servingTime = Math.round((finishedAt - attendedAt) / 1000);
    }

    // Total time: registration to finished (or now if not finished)
    const endTime = customer.status === STATUS.DONE ? finishedAt : now;
    breakdown.totalTime = Math.round((endTime - registeredAt) / 1000);

    return breakdown;
}

/**
 * Format seconds to MM:SS
 */
export function formatTime(seconds) {
    if (!seconds || seconds < 0) return '00:00';

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Get queue statistics for host dashboard
 */
/**
 * Check if a Firestore timestamp is from today
 */
function isToday(timestamp) {
    if (!timestamp) return false;

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const today = new Date();

    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
}

export function getQueueStats(allCustomers) {
    // Fix: CALLING should be counted as WAITING, not serving
    const waiting = allCustomers.filter(c =>
        c.status === STATUS.WAITING ||
        c.status === STATUS.PRE_SERVING ||
        c.status === STATUS.CALLING
    ).length;

    // Fix: Only SERVING status counts as "being served"
    const serving = allCustomers.filter(c => c.status === STATUS.SERVING).length;

    const standby = allCustomers.filter(c => c.status === STATUS.STANDBY).length;

    // Count done customers completed TODAY only
    const done = allCustomers.filter(c =>
        c.status === STATUS.DONE && isToday(c.finishedAt)
    ).length;

    const preferential = allCustomers.filter(c => c.isPreferential && c.status === STATUS.WAITING).length;

    // Get all active customers (not done, not no-show, not standby)
    const activeCustomers = allCustomers.filter(c =>
        [STATUS.WAITING, STATUS.PRE_SERVING, STATUS.CALLING, STATUS.SERVING].includes(c.status)
    );

    // Count of active customers
    const totalActiveCustomers = activeCustomers.length;

    // Calculate total party size for ALL active customers
    const totalPeople = activeCustomers.reduce((sum, c) => sum + (c.partySize || 1), 0);

    return {
        waiting,
        serving,
        standby,
        done,
        preferential,
        totalActiveCustomers,  // NEW: Count of active customers (7 in your example)
        totalPeople,           // Sum of party sizes (19 in your example)
        totalCustomers: allCustomers.length
    };
}

/**
 * Auto-assign algorithm: assign customer to best available seller
 * Uses workload balancing
 */
export function findBestAvailableSeller(sellers, allCustomers) {
    const availableSellers = Object.values(sellers).filter(seller => {
        const status = getSellerStatus(seller, allCustomers);
        return status === SELLER_STATUS.AVAILABLE;
    });

    if (availableSellers.length === 0) {
        return null;
    }

    // Simple round-robin: pick seller with fewest assigned customers
    const sellerWorkload = availableSellers.map(seller => {
        const assignedCount = allCustomers.filter(c =>
            c.assignedTo === seller.email &&
            (c.status === STATUS.PRE_SERVING || c.status === STATUS.WAITING)
        ).length;

        return { seller, assignedCount };
    });

    // Sort by fewest assignments
    sellerWorkload.sort((a, b) => a.assignedCount - b.assignedCount);

    return sellerWorkload[0].seller;
}
